import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { FiFileText, FiUsers, FiCheckSquare, FiCalendar, FiArrowRight } from 'react-icons/fi'
import ReactMarkdown from 'react-markdown'
import { listConversations, getConversation } from '../../api/crm'

type ConversationDetail = {
  id: string
  summary?: string
  action_items?: Array<{ id: string; content?: string; description?: string }>
  clients?: Array<{ id: string; first_name?: string; last_name?: string; email?: string }>
  dates?: Array<{ id: string; title?: string; date?: string }>
}

type Props = {
  userId: string | null
  callStartTime: number
  onComplete: (conversationId: string | null) => void
  isEmbedded?: boolean
}

const POLL_INTERVAL_MS = 3000
const POLL_TIMEOUT_MS = 5 * 60 * 1000

function OnboardingResults({ userId, callStartTime, onComplete, isEmbedded = false }: Props) {
  const [data, setData] = useState<ConversationDetail | null>(null)
  const [timedOut, setTimedOut] = useState(false)
  const pollStartRef = useRef(Date.now())
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const visibleRef = useRef(true)

  const fetchConversation = useCallback(async () => {
    if (!userId) return null
    try {
      const conversations = await listConversations(userId, {})

      const recent = conversations.filter((c) => {
        if (!c.date_created) return false
        return new Date(c.date_created).getTime() >= callStartTime - 60000
      })

      const sorted = recent.toSorted((a, b) => {
        const aTime = a.date_created ? new Date(a.date_created).getTime() : 0
        const bTime = b.date_created ? new Date(b.date_created).getTime() : 0
        return bTime - aTime
      })

      const target = sorted[0]
      if (!target?.id) return null

      const detail = await getConversation(target.id, userId)
      const conv = detail.conversation
      if (!conv?.summary) return null

      return {
        id: target.id,
        summary: conv.summary,
        action_items: detail.action_items || detail.actionItems || [],
        clients: detail.clients || [],
        dates: detail.dates || [],
      } as ConversationDetail
    } catch {
      return null
    }
  }, [userId])

  useEffect(() => {
    const handleVisibility = () => {
      visibleRef.current = document.visibilityState === 'visible'
    }
    document.addEventListener('visibilitychange', handleVisibility)
    return () => document.removeEventListener('visibilitychange', handleVisibility)
  }, [])

  useEffect(() => {
    if (data) return

    const poll = async () => {
      if (!visibleRef.current) return
      if (Date.now() - pollStartRef.current > POLL_TIMEOUT_MS) {
        setTimedOut(true)
        if (timerRef.current) clearInterval(timerRef.current)
        return
      }
      const result = await fetchConversation()
      if (result) {
        setData(result)
        if (timerRef.current) clearInterval(timerRef.current)
      }
    }

    void poll()
    timerRef.current = setInterval(poll, POLL_INTERVAL_MS)
    return () => {
      if (timerRef.current) clearInterval(timerRef.current)
    }
  }, [data, fetchConversation])

  const spinnerVerbs = useMemo(() => [
    'Transcribing your conversation...',
    'Picking out the key details...',
    'Writing up your call notes...',
    'Spotting action items...',
    'Finding contacts mentioned...',
    'Checking for follow-up dates...',
    'Putting it all together...',
  ], [])

  const [verbIndex, setVerbIndex] = useState(0)

  useEffect(() => {
    if (data || timedOut) return
    const advance = () => {
      setVerbIndex((i) => (i + 1) % spinnerVerbs.length)
      const nextDelay = 2000 + Math.random() * 2000
      timer = window.setTimeout(advance, nextDelay)
    }
    let timer = window.setTimeout(advance, 2500 + Math.random() * 1500)
    return () => clearTimeout(timer)
  }, [data, timedOut, spinnerVerbs])

  if (!data && !timedOut) {
    return (
      <div className={isEmbedded ? "onboarding-phase-embedded" : "onboarding-phase"}>
        <div className="onboarding-processing">
          <div className="onboarding-processing-spinner" />
          <h1 className={isEmbedded ? "onboarding-title-small" : "onboarding-title"}>
            Bev is processing your call&hellip;
          </h1>
          <p className="onboarding-spinner-verb" key={verbIndex}>
            {spinnerVerbs[verbIndex]}
          </p>
        </div>

      </div>
    )
  }

  if (timedOut && !data) {
    return (
      <div className={isEmbedded ? "onboarding-phase-embedded" : "onboarding-phase"}>
        <div className="onboarding-processing">
          <h1 className={isEmbedded ? "onboarding-title-small" : "onboarding-title"}>Still processing&hellip;</h1>
          <p className={isEmbedded ? "popup-step-text" : "onboarding-subtitle"}>
            Your call is taking a bit longer than usual. You&apos;ll see your results
            on the home screen shortly.
          </p>
          {!isEmbedded && (
            <button type="button" className="onboarding-btn primary" onClick={() => onComplete(null)}>
              Continue to Scribbl
              <FiArrowRight size={18} />
            </button>
          )}
        </div>
      </div>
    )
  }

  const actionItems = data?.action_items ?? []
  const clients = data?.clients ?? []
  const dates = data?.dates ?? []

  return (
    <div className={isEmbedded ? "onboarding-phase-embedded" : "onboarding-phase"}>
      {!isEmbedded && <h1 className="onboarding-title">Here&apos;s what Bev captured</h1>}

      <div className={isEmbedded ? "onboarding-embedded-note" : "onboarding-results-grid"}>
        {data?.summary && (
          isEmbedded ? (
            <div className="embedded-note-content">
              <ReactMarkdown>{data.summary}</ReactMarkdown>
            </div>
          ) : (
            <div className="onboarding-result-card">
              <div className="onboarding-result-icon"><FiFileText size={20} /></div>
              <h3>Call Note</h3>
              <p className="onboarding-result-content">{data.summary}</p>
            </div>
          )
        )}

        {!isEmbedded && actionItems.length > 0 && (
          <div className="onboarding-result-card">
            <div className="onboarding-result-icon"><FiCheckSquare size={20} /></div>
            <h3>Action Item</h3>
            <ul className="onboarding-result-list">
              {actionItems.slice(0, 5).map((item) => (
                <li key={item.id}>{item.content || item.description}</li>
              ))}
            </ul>
          </div>
        )}

        {!isEmbedded && clients.length > 0 && (
          <div className="onboarding-result-card">
            <div className="onboarding-result-icon"><FiUsers size={20} /></div>
            <h3>Contacts ({clients.length})</h3>
            <ul className="onboarding-result-list">
              {clients.slice(0, 5).map((c) => (
                <li key={c.id}>
                  {[c.first_name, c.last_name].filter(Boolean).join(' ') || c.email || 'Contact'}
                </li>
              ))}
            </ul>
          </div>
        )}

        {!isEmbedded && dates.length > 0 && (
          <div className="onboarding-result-card">
            <div className="onboarding-result-icon"><FiCalendar size={20} /></div>
            <h3>Calendar Events ({dates.length})</h3>
            <ul className="onboarding-result-list">
              {dates.slice(0, 5).map((d) => (
                <li key={d.id}>{d.title || d.date}</li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {!isEmbedded && (
        <p className="onboarding-result-footer">
          That&apos;s it. Every time you call Bev after a visit, this is what happens — your
          notes, action items, and follow-ups, captured automatically.
        </p>
      )}

      {!isEmbedded && (
        <div className="onboarding-actions">
          <button type="button" className="onboarding-btn primary" onClick={() => onComplete(data?.id ?? null)}>
            Now let&apos;s see the notes
            <FiArrowRight size={18} />
          </button>
        </div>
      )}
    </div>
  )
}

export default OnboardingResults
